if (!customElements.get('ai-visualizer')) {
  customElements.define(
    'ai-visualizer',
    class AiVisualizer extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;

        this.abortController = new AbortController();
        this.pipelineController = null;
        this.generationMessageTimers = [];
        this.generationTipTimer = null;
        this.preparationId = 0;
        this.productConfig = this.readProductConfig();
        this.mountShadowRoot();
        this.state = this.createInitialState();

        this.cacheElements();
        if (!this.fileInput || !this.dropZone) return;

        this.bindEvents();
        this.render();
      }

      mountShadowRoot() {
        if (!this.shadowRoot) {
          const shadowRoot = this.attachShadow({ mode: 'open' });
          const hostStyles = document.createElement('style');
          const stylesheet = document.createElement('link');
          const template = document.createElement('template');

          hostStyles.textContent = `
            :host {
              display: block;
              width: 100%;
              min-width: 0;
              max-width: 100%;
            }
          `;
          stylesheet.rel = 'stylesheet';
          stylesheet.href = this.dataset.stylesheetUrl || '';
          template.innerHTML = this.getTemplate();
          shadowRoot.append(hostStyles, stylesheet, template.content.cloneNode(true));
        }

        this.root = this.shadowRoot;
        this.applyUiConfig();
      }

      getTemplate() {
        return `
          <div class="ai-visualizer__shell">
            <div class="ai-visualizer__launch" data-launch-view>
              <div class="ai-visualizer__launch-heading-row" data-launch-heading-row>
                <div class="ai-visualizer__launch-heading-copy">
                  <h2 class="ai-visualizer__heading ai-visualizer__launch-heading" data-launch-heading></h2>
                  <p class="ai-visualizer__product-title" data-launch-product-title></p>
                </div>
                <figure class="ai-visualizer__launch-product-image" data-launch-image-frame hidden>
                  <img data-launch-image loading="lazy" alt="">
                </figure>
              </div>
              <div class="ai-visualizer__launch-copy" data-launch-copy hidden></div>
              <button
                class="ai-visualizer__button ai-visualizer__button--primary ai-visualizer__open"
                type="button"
                aria-controls="ai-visualizer-flow"
                aria-expanded="false"
                data-open-visualizer
              >
                <span data-open-button-text></span>
              </button>
            </div>

            <div id="ai-visualizer-flow" data-flow>
              <header class="ai-visualizer__header" data-flow-header>
                <p class="ai-visualizer__eyebrow">AI-visualisering</p>
                <h2 class="ai-visualizer__heading" data-flow-heading></h2>
                <p class="ai-visualizer__product-title" data-flow-product-title></p>
                <div class="ai-visualizer__supporting-text" data-supporting-text hidden></div>
              </header>

              <p class="ai-visualizer__error" data-error role="alert" hidden></p>

              <div data-upload-view>
                <input
                  id="ai-visualizer-upload"
                  class="ai-visualizer__visually-hidden"
                  type="file"
                  accept="image/*"
                  aria-label="Välj en bild av ditt rum"
                  data-file-input
                >

                <div class="ai-visualizer__drop-zone ai-visualizer__surface" data-drop-zone>
                  <span class="ai-visualizer__upload-icon" aria-hidden="true">
                    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 21V7m0 0-5 5m5-5 5 5M8 18v5.5A2.5 2.5 0 0 0 10.5 26h11a2.5 2.5 0 0 0 2.5-2.5V18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                  <div class="ai-visualizer__drop-copy">
                    <h3 class="ai-visualizer__drop-heading">Ladda upp en bild av ditt rum</h3>
                    <p class="ai-visualizer__desktop-instruction">
                      Dra och släpp en bild här, eller välj en bild från din enhet.
                    </p>
                    <p class="ai-visualizer__mobile-instruction">Välj en bild från din enhet.</p>
                  </div>
                  <button class="ai-visualizer__button ai-visualizer__button--secondary" type="button" data-choose-photo>
                    <span>Välj foto</span>
                  </button>
                  <p class="ai-visualizer__file-hint">Endast bildfiler</p>
                </div>
              </div>

              <div class="ai-visualizer__editor" data-editor-view hidden>
                <div class="ai-visualizer__preview-panel ai-visualizer__surface">
                  <figure class="ai-visualizer__preview-frame">
                    <img data-preview-image width="1" height="1" alt="Uppladdad bild av ditt rum">
                  </figure>
                  <div class="ai-visualizer__preview-meta">
                    <div class="ai-visualizer__preview-actions">
                      <button class="ai-visualizer__button ai-visualizer__button--secondary ai-visualizer__button--small" type="button" data-replace-photo>
                        <span>Byt foto</span>
                      </button>
                      <button class="ai-visualizer__button ai-visualizer__button--plain" type="button" data-remove-photo>Ta bort</button>
                    </div>
                  </div>
                </div>

                <div class="ai-visualizer__controls">
                  <div class="ai-visualizer__loading" data-image-preparing role="status" aria-live="polite" hidden>
                    ${this.getSpinnerMarkup('ai-visualizer__loading-spinner')}
                    <span>Förbereder bilden&hellip;</span>
                  </div>

                  <fieldset class="ai-visualizer__surface-fieldset" data-surface-fieldset hidden>
                    <legend class="ai-visualizer__surface-heading">Vilken yta vill du ändra?</legend>
                    <div class="ai-visualizer__surface-options" data-surface-options></div>
                    <div class="ai-visualizer__prompt-field">
                      <label class="ai-visualizer__visually-hidden" for="ai-visualizer-prompt">
                        Beskriv vad du vill förändra (valfritt)
                      </label>
                      <input
                        id="ai-visualizer-prompt"
                        class="ai-visualizer__input"
                        type="text"
                        maxlength="150"
                        autocomplete="off"
                        placeholder="Beskriv mer (valfritt)"
                        aria-describedby="ai-visualizer-prompt-helper ai-visualizer-prompt-counter"
                        data-custom-prompt
                      >
                      <div class="ai-visualizer__prompt-meta">
                        <span id="ai-visualizer-prompt-counter" data-custom-prompt-counter>0/150</span>
                      </div>
                    </div>
                  </fieldset>

                  <div class="ai-visualizer__loading" data-loading-status role="status" aria-live="polite" hidden>
                    ${this.getSpinnerMarkup('ai-visualizer__loading-spinner')}
                    <span data-loading-message>Vi skapar din visualisering&hellip;</span>
                  </div>

                  <button
                    class="ai-visualizer__button ai-visualizer__button--primary ai-visualizer__generate"
                    type="button"
                    data-generate
                    disabled
                  >
                    <span data-generate-button-text></span>
                    ${this.getSpinnerMarkup('ai-visualizer__button-spinner')}
                  </button>
                </div>
              </div>

              <div class="ai-visualizer__generation" data-generation-view hidden>
                <div class="ai-visualizer__generation-canvas ai-visualizer__surface" data-generation-canvas>
                  <img class="ai-visualizer__generation-image" data-generation-image width="1" height="1" alt="">
                  <div class="ai-visualizer__generation-shimmer" aria-hidden="true"></div>
                  <div class="ai-visualizer__generation-status">
                    <div class="ai-visualizer__generation-status-copy" role="status" aria-live="polite" aria-atomic="true">
                      <div class="ai-visualizer__generation-indicator" aria-hidden="true">
                        <span class="ai-visualizer__generation-tiles">
                          <span class="ai-visualizer__generation-tile ai-visualizer__generation-tile--one"></span>
                          <span class="ai-visualizer__generation-tile ai-visualizer__generation-tile--two"></span>
                          <span class="ai-visualizer__generation-tile ai-visualizer__generation-tile--three"></span>
                          <span class="ai-visualizer__generation-tile ai-visualizer__generation-tile--four"></span>
                        </span>
                      </div>
                      <p class="ai-visualizer__generation-message" data-generation-message>Förbereder material&hellip;</p>
                      <div class="ai-visualizer__generation-progress" aria-hidden="true"><span></span></div>
                      <p class="ai-visualizer__generation-helper">Detta kan ta upp till 30–40 sekunder.</p>
                    </div>
                    <div class="ai-visualizer__generation-tip" data-generation-tip>
                      <p class="ai-visualizer__generation-tip-label">Visste du att?</p>
                      <p class="ai-visualizer__generation-tip-text" data-generation-tip-text></p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="ai-visualizer__result"
                role="region"
                aria-labelledby="ai-visualizer-result-heading"
                data-result-view
                hidden
              >
                <div class="ai-visualizer__result-header">
                  <p class="ai-visualizer__eyebrow" data-result-surface>Visualiserat resultat</p>
                  <h3 id="ai-visualizer-result-heading" class="ai-visualizer__result-heading" data-result-heading tabindex="-1">Din visualisering</h3>
                  <p class="ai-visualizer__result-help">Dra reglaget för att jämföra originalet med visualiseringen.</p>
                </div>

                <div class="ai-visualizer__comparison ai-visualizer__surface" data-comparison>
                  <img class="ai-visualizer__comparison-image" data-before-image width="1" height="1" alt="Originalbild av ditt rum">
                  <div class="ai-visualizer__comparison-after" aria-hidden="true">
                    <img class="ai-visualizer__comparison-image" data-result-image width="1" height="1">
                  </div>
                  <span class="ai-visualizer__comparison-label ai-visualizer__comparison-label--after">Efter</span>
                  <span class="ai-visualizer__comparison-label ai-visualizer__comparison-label--before">Före</span>
                  <span class="ai-visualizer__comparison-divider" aria-hidden="true">
                    <span class="ai-visualizer__comparison-handle">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="m9 8-4 4 4 4m6-8 4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                  </span>
                  <input
                    class="ai-visualizer__comparison-range"
                    type="range"
                    min="0"
                    max="100"
                    value="50"
                    aria-label="Dra för att jämföra före och efter"
                    data-comparison-range
                  >
                </div>

                <div class="ai-visualizer__result-footer">
                  <div class="ai-visualizer__result-actions">
                    <button class="ai-visualizer__button ai-visualizer__button--secondary" type="button" data-download-result>
                      <span>Ladda ner bild</span>
                    </button>
                    <button class="ai-visualizer__button ai-visualizer__button--plain" type="button" data-restart>Börja om</button>
                  </div>
                </div>
              </div>

              <p class="ai-visualizer__disclaimer" data-disclaimer hidden></p>
            </div>
          </div>
        `;
      }

      getSpinnerMarkup(className) {
        return `
          <svg class="ai-visualizer__spinner ${className}" viewBox="0 0 50 50" aria-hidden="true" focusable="false">
            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="4"></circle>
          </svg>
        `;
      }

      applyUiConfig() {
        const ui = this.productConfig.ui;
        const compact = this.hasAttribute('data-compact');
        const headingHtml = ui.headingHtml || ui.headingText;
        const headingText = ui.headingText || 'AI-visualisering';
        const launchHeading = this.root.querySelector('[data-launch-heading]');
        const flowHeading = this.root.querySelector('[data-flow-heading]');
        const launchCopy = this.root.querySelector('[data-launch-copy]');
        const supportingText = this.root.querySelector('[data-supporting-text]');
        const launchImage = this.root.querySelector('[data-launch-image]');
        const launchImageFrame = this.root.querySelector('[data-launch-image-frame]');
        const launchHeadingRow = this.root.querySelector('[data-launch-heading-row]');

        [launchHeading, flowHeading].forEach((heading) => {
          heading.innerHTML = headingHtml;
        });
        this.root.querySelector('[data-launch-product-title]').textContent = this.productConfig.title;
        this.root.querySelector('[data-flow-product-title]').textContent = this.productConfig.title;
        this.root.querySelector('[data-open-button-text]').textContent = ui.launchButtonText;
        this.root.querySelector('[data-generate-button-text]').textContent = ui.generateButtonText;
        this.root.querySelector('[data-disclaimer]').textContent = ui.disclaimerText;
        this.root.querySelector('[data-flow-header]').hidden = compact;
        this.root.querySelector('[data-result-image]').alt = `AI-visualisering med ${this.productConfig.title}`;
        this.setAttribute('role', 'region');
        this.setAttribute('aria-label', headingText);

        [launchCopy, supportingText].forEach((copy) => {
          copy.innerHTML = ui.supportingHtml;
          copy.hidden = !ui.supportingHtml.trim();
        });

        if (ui.launchImage && ui.launchImage.url) {
          launchImage.src = ui.launchImage.url;
          launchImage.width = ui.launchImage.width || 200;
          launchImage.height = ui.launchImage.height || 200;
          launchImageFrame.hidden = false;
          launchHeadingRow.classList.add('has-image');
        } else {
          launchImage.removeAttribute('src');
          launchImageFrame.hidden = true;
          launchHeadingRow.classList.remove('has-image');
        }
      }

      disconnectedCallback() {
        if (this.abortController) this.abortController.abort();
        if (this.pipelineController) this.pipelineController.abort();
        this.clearGenerationLoadingTimers();
        this.abortController = null;
        this.pipelineController = null;
        this.preparationId += 1;
        this.revokeUploadedImage();
        this.revokeResultImage();
        this.productConfig = null;
        this.state = null;
      }

      createInitialState() {
        return {
          expanded: !this.hasAttribute('data-compact'),
          pane: 'upload',
          uploadedImage: null,
          surfaces: [],
          analysisStatus: 'idle',
          selectedSurface: null,
          imagePreparing: false,
          analysisPending: false,
          generationPending: false,
          loadingMessage: '',
          generationTipIndex: 0,
          resultImage: null,
          resultImageMimeType: '',
        };
      }

      readProductConfig() {
        const configElement = this.querySelector('[data-visualizer-config]');
        const emptyConfig = {
          handle: '',
          title: '',
          materialType: 'tile',
          referenceImageUrls: [],
          tileWidthCm: null,
          tileHeightCm: null,
          finish: null,
          color: null,
          ui: {
            headingHtml: 'Se plattan i ditt hem med AI',
            headingText: 'Se plattan i ditt hem med AI',
            supportingHtml: '',
            launchButtonText: 'Visualisera i ditt rum',
            generateButtonText: 'Skapa visualisering',
            disclaimerText: '',
            launchImage: null,
          },
        };
        if (!configElement) return emptyConfig;

        try {
          const config = JSON.parse(configElement.textContent);
          const product = config.product && typeof config.product === 'object' ? config.product : {};
          const ui = config.ui && typeof config.ui === 'object' ? config.ui : {};
          const materialType = this.normalizeMaterialType(product.materialType);
          const tileDimensions = this.parseTileDimensions(product.tileSize);
          const launchImage = ui.launchImage && typeof ui.launchImage === 'object' ? ui.launchImage : null;
          const defaultHeading =
            materialType === 'vinyl_flooring' ? 'Se golvet i ditt hem med AI' : emptyConfig.ui.headingText;
          return {
            handle: typeof product.handle === 'string' ? product.handle : '',
            title: typeof product.title === 'string' ? product.title : '',
            materialType,
            referenceImageUrls: Array.isArray(product.referenceImageUrls)
              ? product.referenceImageUrls
                  .filter((url) => typeof url === 'string' && url.length > 0)
                  .slice(0, 4)
              : [],
            tileWidthCm: tileDimensions ? tileDimensions.width : null,
            tileHeightCm: tileDimensions ? tileDimensions.height : null,
            finish: typeof product.finish === 'string' && product.finish.trim() ? product.finish.trim() : null,
            color: typeof product.color === 'string' && product.color.trim() ? product.color.trim() : null,
            ui: {
              headingHtml:
                typeof ui.headingHtml === 'string' && ui.headingHtml.trim()
                  ? ui.headingHtml
                  : defaultHeading,
              headingText:
                typeof ui.headingText === 'string' && ui.headingText.trim()
                  ? ui.headingText.trim()
                  : defaultHeading,
              supportingHtml: typeof ui.supportingHtml === 'string' ? ui.supportingHtml : '',
              launchButtonText:
                typeof ui.launchButtonText === 'string' && ui.launchButtonText.trim()
                  ? ui.launchButtonText.trim()
                  : emptyConfig.ui.launchButtonText,
              generateButtonText:
                typeof ui.generateButtonText === 'string' && ui.generateButtonText.trim()
                  ? ui.generateButtonText.trim()
                  : emptyConfig.ui.generateButtonText,
              disclaimerText: typeof ui.disclaimerText === 'string' ? ui.disclaimerText.trim() : '',
              launchImage:
                launchImage && typeof launchImage.url === 'string' && launchImage.url
                  ? {
                      url: launchImage.url,
                      width: Number(launchImage.width) || 200,
                      height: Number(launchImage.height) || 200,
                    }
                  : null,
            },
          };
        } catch (error) {
          return emptyConfig;
        }
      }

      normalizeMaterialType(materialType) {
        return typeof materialType === 'string' && materialType.trim().toLowerCase() === 'vinyl_flooring'
          ? 'vinyl_flooring'
          : 'tile';
      }

      parseTileDimensions(tileSize) {
        if (typeof tileSize !== 'string') return null;

        const match = tileSize.trim().match(/^(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(?:cm)?$/i);
        if (!match) return null;

        const width = Number(match[1].replace(',', '.'));
        const height = Number(match[2].replace(',', '.'));
        return width > 0 && height > 0 && Number.isFinite(width) && Number.isFinite(height)
          ? { width, height }
          : null;
      }

      cacheElements() {
        this.fileInput = this.root.querySelector('[data-file-input]');
        this.dropZone = this.root.querySelector('[data-drop-zone]');
        this.launchView = this.root.querySelector('[data-launch-view]');
        this.flow = this.root.querySelector('[data-flow]');
        this.openButton = this.root.querySelector('[data-open-visualizer]');
        this.uploadView = this.root.querySelector('[data-upload-view]');
        this.editorView = this.root.querySelector('[data-editor-view]');
        this.generationView = this.root.querySelector('[data-generation-view]');
        this.generationCanvas = this.root.querySelector('[data-generation-canvas]');
        this.generationImage = this.root.querySelector('[data-generation-image]');
        this.resultView = this.root.querySelector('[data-result-view]');
        this.previewImage = this.root.querySelector('[data-preview-image]');
        this.error = this.root.querySelector('[data-error]');
        this.generateButton = this.root.querySelector('[data-generate]');
        this.imagePreparingStatus = this.root.querySelector('[data-image-preparing]');
        this.loadingStatus = this.root.querySelector('[data-loading-status]');
        this.loadingMessage = this.root.querySelector('[data-loading-message]');
        this.generationMessage = this.root.querySelector('[data-generation-message]');
        this.generationTip = this.root.querySelector('[data-generation-tip]');
        this.generationTipText = this.root.querySelector('[data-generation-tip-text]');
        this.surfaceFieldset = this.root.querySelector('[data-surface-fieldset]');
        this.surfaceOptions = this.root.querySelector('[data-surface-options]');
        this.surfaceInputs = [];
        this.customPromptInput = this.root.querySelector('[data-custom-prompt]');
        this.customPromptCounter = this.root.querySelector('[data-custom-prompt-counter]');
        this.replaceButton = this.root.querySelector('[data-replace-photo]');
        this.removeButton = this.root.querySelector('[data-remove-photo]');
        this.beforeImage = this.root.querySelector('[data-before-image]');
        this.resultImage = this.root.querySelector('[data-result-image]');
        this.resultSurface = this.root.querySelector('[data-result-surface]');
        this.resultHeading = this.root.querySelector('[data-result-heading]');
        this.comparison = this.root.querySelector('[data-comparison]');
        this.comparisonRange = this.root.querySelector('[data-comparison-range]');
        this.downloadButton = this.root.querySelector('[data-download-result]');
        this.disclaimer = this.root.querySelector('[data-disclaimer]');
      }

      bindEvents() {
        const signal = this.abortController.signal;

        this.root.querySelectorAll('[data-choose-photo], [data-replace-photo]').forEach((button) => {
          button.addEventListener('click', () => this.openFilePicker(), { signal });
        });

        if (this.openButton) {
          this.openButton.addEventListener('click', () => this.expand(), { signal });
        }

        this.fileInput.addEventListener(
          'change',
          (event) => {
            const file = event.target.files && event.target.files[0];
            if (file) this.selectFile(file);
          },
          { signal }
        );

        ['dragenter', 'dragover'].forEach((eventName) => {
          this.dropZone.addEventListener(
            eventName,
            (event) => {
              event.preventDefault();
              if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
              this.dropZone.classList.add('is-dragging');
            },
            { signal }
          );
        });

        this.dropZone.addEventListener(
          'dragleave',
          (event) => {
            if (!this.dropZone.contains(event.relatedTarget)) {
              this.dropZone.classList.remove('is-dragging');
            }
          },
          { signal }
        );

        this.dropZone.addEventListener(
          'drop',
          (event) => {
            event.preventDefault();
            this.dropZone.classList.remove('is-dragging');
            const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
            if (file) this.selectFile(file);
          },
          { signal }
        );

        this.surfaceOptions.addEventListener(
          'change',
          (event) => {
            const input = event.target.closest('[data-surface]');
            if (!input || !input.checked) return;
            this.state.selectedSurface = input.value;
            this.render();
          },
          { signal }
        );

        this.customPromptInput.addEventListener('input', () => this.updateCustomPromptCounter(), { signal });

        this.removeButton.addEventListener('click', () => this.reset(), { signal });
        this.generateButton.addEventListener(
          'click',
          () => {
            if (this.state.analysisStatus === 'ready') {
              this.generateVisualization();
              return;
            }

            this.analyzeSelectedRoom();
          },
          { signal }
        );
        this.root.querySelector('[data-restart]').addEventListener('click', () => this.reset(true), { signal });
        this.downloadButton.addEventListener('click', () => this.downloadResult(), { signal });

        this.comparisonRange.addEventListener(
          'input',
          () => this.setComparisonPosition(this.comparisonRange.value),
          { signal }
        );
      }

      openFilePicker() {
        if (this.isPipelinePending()) return;
        this.fileInput.value = '';
        this.fileInput.click();
      }

      isPipelinePending() {
        return Boolean(this.state && (this.state.analysisPending || this.state.generationPending));
      }

      expand() {
        if (this.state.expanded) return;

        this.state.expanded = true;
        this.render();

        window.requestAnimationFrame(() => {
          const chooseButton = this.root.querySelector('[data-choose-photo]');
          if (chooseButton) chooseButton.focus();
        });
      }

      async selectFile(file) {
        if (!file.type || !file.type.startsWith('image/')) {
          this.showError('Välj en giltig bildfil för att fortsätta.');
          this.fileInput.value = '';
          return;
        }

        const preparationId = ++this.preparationId;
        this.cancelPipeline();
        this.hideError();
        this.revokeUploadedImage();
        this.revokeResultImage();

        const previewUrl = URL.createObjectURL(file);
        this.state.uploadedImage = {
          file,
          previewUrl,
          preparedBlob: file,
          width: null,
          height: null,
        };
        this.state.pane = 'configure';
        this.state.imagePreparing = true;
        this.state.surfaces = [];
        this.state.analysisStatus = 'idle';
        this.state.selectedSurface = null;
        this.state.resultImage = null;
        this.state.resultImageMimeType = '';
        this.renderSurfaceOptions([]);
        this.render();

        let preparedImage = null;

        try {
          preparedImage = await this.prepareImage(file, previewUrl);
        } catch (error) {
          // The original remains usable if the browser cannot decode or resize
          // a particular image format locally.
        }

        if (preparationId !== this.preparationId || !this.state || !this.state.uploadedImage) return;
        if (preparedImage) Object.assign(this.state.uploadedImage, preparedImage);

        this.state.imagePreparing = false;
        this.render();
        await this.analyzeSelectedRoom(preparationId);
      }

      async prepareImage(file, previewUrl, maxDimension = 2048) {
        const decodedImage = await this.decodeImage(file, previewUrl);

        try {
          const scale = Math.min(1, maxDimension / Math.max(decodedImage.width, decodedImage.height));
          const width = Math.max(1, Math.round(decodedImage.width * scale));
          const height = Math.max(1, Math.round(decodedImage.height * scale));

          if (scale === 1) {
            return { preparedBlob: file, width, height };
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Canvas is not available');

          context.drawImage(decodedImage.source, 0, 0, width, height);
          const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
          const preparedBlob = await new Promise((resolve, reject) => {
            canvas.toBlob(
              (blob) => (blob ? resolve(blob) : reject(new Error('Image resizing failed'))),
              outputType,
              0.88
            );
          });

          canvas.width = 0;
          canvas.height = 0;
          return { preparedBlob, width, height };
        } finally {
          decodedImage.release();
        }
      }

      async decodeImage(file, previewUrl) {
        if ('createImageBitmap' in window) {
          try {
            const bitmap = await window.createImageBitmap(file);
            return {
              source: bitmap,
              width: bitmap.width,
              height: bitmap.height,
              release: () => bitmap.close(),
            };
          } catch (error) {
            // Fall through to an HTMLImageElement for formats handled by the
            // browser's image element but not createImageBitmap.
          }
        }

        const image = await new Promise((resolve, reject) => {
          const element = new Image();
          element.onload = () => resolve(element);
          element.onerror = () => reject(new Error('Image could not be decoded'));
          element.src = previewUrl;
        });

        return {
          source: image,
          width: image.naturalWidth,
          height: image.naturalHeight,
          release: () => {},
        };
      }

      async analyzeSelectedRoom(expectedPreparationId = this.preparationId) {
        if (
          !this.state.uploadedImage ||
          this.state.imagePreparing ||
          this.isPipelinePending() ||
          expectedPreparationId !== this.preparationId
        ) {
          return;
        }

        this.hideError();
        this.cancelPipeline();
        this.pipelineController = new AbortController();
        const { signal } = this.pipelineController;

        this.state.pane = 'loading';
        this.state.analysisPending = true;
        this.state.loadingMessage = 'Analyserar rummet…';
        this.state.analysisStatus = 'loading';
        this.state.surfaces = [];
        this.state.selectedSurface = null;
        this.renderSurfaceOptions([]);
        this.render();

        try {
          const roomAnalysis = await this.analyzeRoom(this.state.uploadedImage, signal);
          if (signal.aborted || !this.state || expectedPreparationId !== this.preparationId) return;

          const surfaces = this.getSelectableSurfaces(this.extractSurfaces(roomAnalysis));
          if (!surfaces.length) {
            const error = new Error('The analysis response did not contain any surfaces');
            error.userMessage =
              this.productConfig.materialType === 'vinyl_flooring'
                ? 'Ingen lämplig golvyta kunde identifieras i bilden. Prova gärna en annan rumsbild.'
                : 'Inga redigerbara ytor hittades i bilden. Prova gärna med ett annat foto.';
            throw error;
          }

          this.state.surfaces = surfaces;
          this.state.analysisStatus = 'ready';
          this.state.analysisPending = false;
          this.state.loadingMessage = '';
          this.state.pane = 'configure';
          this.renderSurfaceOptions(surfaces);
          this.render();
        } catch (error) {
          if (error.name === 'AbortError' || !this.state) return;
          this.state.analysisPending = false;
          this.state.loadingMessage = '';
          this.state.analysisStatus = 'error';
          this.state.surfaces = [];
          this.state.pane = 'configure';
          this.showError(error.userMessage || 'Rummet kunde inte analyseras. Försök igen.');
          this.render();
        } finally {
          if (this.pipelineController && this.pipelineController.signal === signal) {
            this.pipelineController = null;
          }
        }
      }

      async analyzeRoom(uploadedImage, signal) {
        const formData = new FormData();
        formData.append('image', uploadedImage.preparedBlob, this.getPreparedFilename(uploadedImage));

        const response = await fetch(this.getApiUrl('/api/analyze-room'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
          signal,
        });
        const responseBody = await response.json().catch(() => null);

        if (!response.ok) {
          const error = new Error(`Room analysis failed with status ${response.status}`);
          const apiMessage = this.getApiErrorMessage(responseBody);
          error.userMessage = apiMessage
            ? `Rummet kunde inte analyseras: ${apiMessage}`
            : 'Rummet kunde inte analyseras. Försök igen.';
          throw error;
        }

        if (!responseBody || typeof responseBody !== 'object') {
          const error = new Error('Room analysis returned an invalid JSON response');
          error.userMessage = 'Rumsanalysen gav ett ogiltigt svar. Försök igen.';
          throw error;
        }

        return responseBody;
      }

      getApiUrl(path) {
        const apiBaseUrl = (this.dataset.apiBaseUrl || '').trim().replace(/\/+$/, '');
        return apiBaseUrl ? `${apiBaseUrl}${path}` : path;
      }

      getPreparedFilename(uploadedImage) {
        const extensionByType = {
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/webp': 'webp',
        };
        const extension = extensionByType[uploadedImage.preparedBlob.type];
        const originalName = uploadedImage.file.name || 'room-image';
        const basename = originalName.replace(/\.[^.]+$/, '') || 'room-image';

        return extension ? `${basename}.${extension}` : originalName;
      }

      getApiErrorMessage(responseBody) {
        if (!responseBody || typeof responseBody !== 'object') return '';
        if (typeof responseBody.error === 'string') return responseBody.error;
        if (responseBody.error && typeof responseBody.error.message === 'string') {
          return responseBody.error.message;
        }
        return typeof responseBody.message === 'string' ? responseBody.message : '';
      }

      extractSurfaces(roomAnalysis) {
        const analysis =
          roomAnalysis.analysis ||
          (roomAnalysis.data && roomAnalysis.data.analysis) ||
          roomAnalysis.result ||
          roomAnalysis.data ||
          roomAnalysis;
        const responseSurfaces =
          analysis.surfaces || analysis.detectedSurfaces || analysis.detected_surfaces || analysis.editableSurfaces || [];
        if (!Array.isArray(responseSurfaces)) return [];

        const usedValues = new Set();

        return responseSurfaces
          .map((surface, index) => {
            if (typeof surface === 'string') {
              const value = surface.trim();
              if (!value || usedValues.has(value)) return null;
              usedValues.add(value);
              return { value, label: value, baseType: value };
            }

            if (!surface || typeof surface !== 'object') return null;
            const rawValue =
              surface.id ||
              surface.surfaceId ||
              surface.surface_id ||
              surface.value ||
              surface.type ||
              surface.surfaceType ||
              surface.surface_type ||
              surface.name ||
              surface.surface;
            const rawLabel =
              surface.label ||
              surface.displayName ||
              surface.display_name ||
              surface.name ||
              surface.type ||
              surface.surfaceType ||
              surface.surface_type ||
              rawValue;
            const rawBaseType =
              surface.baseType ||
              surface.base_type ||
              surface.surfaceBaseType ||
              surface.surface_base_type ||
              surface.type ||
              surface.surfaceType ||
              surface.surface_type;
            const value = String(rawValue || `surface-${index + 1}`).trim();
            const label = String(rawLabel || value).trim();
            const baseType = String(rawBaseType || '').trim();

            if (!value || !label || usedValues.has(value)) return null;
            usedValues.add(value);
            return { value, label, baseType };
          })
          .filter(Boolean);
      }

      getSelectableSurfaces(surfaces) {
        return this.productConfig.materialType === 'vinyl_flooring'
          ? surfaces.filter((surface) => surface.baseType === 'floor')
          : surfaces;
      }

      renderSurfaceOptions(surfaces) {
        this.surfaceOptions.replaceChildren();
        this.surfaceInputs = [];
        const fragment = document.createDocumentFragment();
        const instanceId = (this.dataset.sectionId || 'visualizer').replace(/[^a-zA-Z0-9_-]/g, '-');
        const inputName = `AiVisualizerSurface-${this.dataset.sectionId || 'visualizer'}`;

        surfaces.forEach((surface, index) => {
          const option = document.createElement('div');
          option.className = 'ai-visualizer__surface-option';

          const input = document.createElement('input');
          input.id = `AiVisualizerSurface-${instanceId}-${index + 1}`;
          input.type = 'radio';
          input.name = inputName;
          input.value = surface.value;
          input.dataset.surface = '';

          const label = document.createElement('label');
          label.htmlFor = input.id;
          label.textContent = surface.label;

          option.append(input, label);
          fragment.appendChild(option);
          this.surfaceInputs.push(input);
        });

        this.surfaceOptions.appendChild(fragment);
      }

      async generateVisualization() {
        const selectedSurface = this.state.surfaces.find(
          (surface) => surface.value === this.state.selectedSurface
        );

        if (
          !this.state.uploadedImage ||
          this.state.analysisStatus !== 'ready' ||
          !selectedSurface ||
          this.state.imagePreparing ||
          this.isPipelinePending()
        ) {
          return;
        }

        if (
          this.productConfig.materialType === 'tile' &&
          (!this.productConfig.tileWidthCm || !this.productConfig.tileHeightCm)
        ) {
          this.showError('Produktens plattmått saknas eller har ett ogiltigt format.');
          return;
        }

        if (!selectedSurface.baseType) {
          this.showError('Den valda ytan saknar en giltig yttyp. Analysera rummet igen.');
          return;
        }

        if (this.productConfig.materialType === 'vinyl_flooring' && selectedSurface.baseType !== 'floor') {
          this.showError('Vinylgolv kan endast visualiseras på en golvyta.');
          return;
        }

        this.hideError();
        this.cancelPipeline();
        this.pipelineController = new AbortController();
        const { signal } = this.pipelineController;

        this.state.pane = 'loading';
        this.state.generationPending = true;
        this.startGenerationMessageSequence();
        this.startGenerationTipRotation();
        this.render();

        try {
          const response = await this.requestVisualization(
            {
              productHandle: this.productConfig.handle,
              productTitle: this.productConfig.title,
              materialType: this.productConfig.materialType,
              referenceImageUrls: this.productConfig.referenceImageUrls,
              tileWidthCm: this.productConfig.tileWidthCm,
              tileHeightCm: this.productConfig.tileHeightCm,
              finish: this.productConfig.finish,
              color: this.productConfig.color,
              uploadedImage: this.state.uploadedImage,
              selectedSurface,
              customPrompt: this.customPromptInput.value.trim(),
            },
            { signal }
          );

          if (signal.aborted || !this.state) return;
          this.revokeResultImage();
          this.state.generationPending = false;
          this.state.loadingMessage = '';
          this.state.resultImage = URL.createObjectURL(response.imageBlob);
          this.state.resultImageMimeType = response.mimeType;
          this.state.pane = 'result';
          this.comparisonRange.value = '50';
          this.setComparisonPosition(50);
          this.render();
          this.resultHeading.focus();
        } catch (error) {
          if (error.name === 'AbortError' || !this.state) return;
          this.state.generationPending = false;
          this.state.loadingMessage = '';
          this.state.pane = 'configure';
          this.showError(error.userMessage || 'Visualiseringen kunde inte skapas. Försök igen.');
          this.render();
        } finally {
          this.clearGenerationLoadingTimers();
          if (this.pipelineController && this.pipelineController.signal === signal) {
            this.pipelineController = null;
          }
        }
      }

      async requestVisualization(payload, { signal }) {
        const referenceImages = await this.fetchReferenceImages(payload.referenceImageUrls, signal);
        const formData = new FormData();
        const { selectedSurface } = payload;
        const customInstruction =
          typeof payload.customPrompt === 'string' ? payload.customPrompt.trim() : '';

        formData.append('image', payload.uploadedImage.preparedBlob, this.getPreparedFilename(payload.uploadedImage));
        formData.append('surfaceId', selectedSurface.value);
        formData.append('surfaceLabel', selectedSurface.label);
        formData.append('surfaceBaseType', selectedSurface.baseType);
        if (customInstruction) formData.append('customInstruction', customInstruction);
        formData.append('productHandle', payload.productHandle);
        formData.append('productTitle', payload.productTitle);
        formData.append('materialType', this.normalizeMaterialType(payload.materialType));
        if (payload.tileWidthCm && payload.tileHeightCm) {
          formData.append('tileWidthCm', String(payload.tileWidthCm));
          formData.append('tileHeightCm', String(payload.tileHeightCm));
        }
        if (payload.finish) formData.append('finish', payload.finish);
        if (payload.color) formData.append('color', payload.color);
        referenceImages.forEach(({ blob, filename }) => {
          formData.append('referenceImages', blob, filename);
        });

        let responseBody;

        try {
          responseBody = await this.runWithTimeout(
            signal,
            120000,
            'Bildgenereringen tog för lång tid. Försök igen.',
            async (requestSignal) => {
              const requestResponse = await fetch(this.getApiUrl('/api/visualize'), {
                method: 'POST',
                headers: { Accept: 'application/json' },
                body: formData,
                signal: requestSignal,
              });

              if (!requestResponse.ok) {
                if (requestResponse.status === 429) {
                  throw this.createVisualizationApiError(requestResponse, null);
                }

                const errorBody = await requestResponse.json().catch(() => null);
                throw this.createVisualizationApiError(requestResponse, errorBody);
              }

              return requestResponse.json().catch(() => null);
            }
          );
        } catch (error) {
          if (error.name !== 'AbortError' && !error.userMessage && error instanceof TypeError) {
            error.userMessage = 'Kunde inte nå bildtjänsten. Kontrollera anslutningen och försök igen.';
          }
          throw error;
        }

        return this.parseVisualizationResponse(responseBody);
      }

      startGenerationMessageSequence() {
        this.clearGenerationMessageTimers();

        const stages = [
          { delay: 0, message: 'Förbereder material…' },
          { delay: 5000, message: 'Analyserar vald yta…' },
          { delay: 11000, message: 'Skapar visualisering…' },
          { delay: 26000, message: 'Slutför bilden…' },
        ];

        this.state.loadingMessage = stages[0].message;
        this.generationMessageTimers = stages.slice(1).map(({ delay, message }) =>
          window.setTimeout(() => {
            if (!this.state || !this.state.generationPending) return;
            this.state.loadingMessage = message;
            this.render();
          }, delay)
        );
      }

      clearGenerationMessageTimers() {
        if (!this.generationMessageTimers) return;
        this.generationMessageTimers.forEach((timer) => window.clearTimeout(timer));
        this.generationMessageTimers = [];
      }

      getGenerationTips() {
        if (this.productConfig.materialType === 'vinyl_flooring') {
          return [
            'Olika ljus kan påverka hur golvets färg och struktur upplevs.',
            'Ett sammanhängande golv kan skapa ett lugnare uttryck i rummet.',
            'Rummets ljus och möbler påverkar hur golvet upplevs.',
            'Jämför gärna visualiseringen med produktens referensbilder.',
            'Beställ gärna ett varuprov – färg och struktur kan upplevas olika i olika ljus.',
            'AI-visualiseringen är en inspirationsbild och det faktiska resultatet kan variera.',
          ];
        }

        return [
          'Klinker kan användas på både golv och vägg.',
          'Rektifierade kanter innebär att plattan är precisionsskuren för rakare foglinjer.',
          'Våra keramiska plattor följer gällande EU-standarder.',
          'Större plattor ger färre fogar och kan skapa ett lugnare uttryck.',
          'Beställ gärna ett varuprov – färg och struktur kan upplevas olika i olika ljus.',
          'Golvvärme fungerar utmärkt tillsammans med många typer av klinker.',
        ];
      }

      startGenerationTipRotation() {
        this.clearGenerationTipTimer();
        if (!this.state || !this.state.generationPending) return;

        const tips = this.getGenerationTips();
        this.state.generationTipIndex = 0;
        this.generationTipTimer = window.setInterval(() => {
          if (!this.state || !this.state.generationPending) return;
          this.state.generationTipIndex = (this.state.generationTipIndex + 1) % tips.length;
          this.render();
        }, 5000);
      }

      clearGenerationTipTimer() {
        if (this.generationTipTimer !== null && this.generationTipTimer !== undefined) {
          window.clearInterval(this.generationTipTimer);
        }
        this.generationTipTimer = null;
      }

      clearGenerationLoadingTimers() {
        this.clearGenerationMessageTimers();
        this.clearGenerationTipTimer();
      }

      async fetchReferenceImages(referenceImageUrls, signal) {
        if (!Array.isArray(referenceImageUrls) || referenceImageUrls.length === 0) {
          const error = new Error('No product reference images are configured');
          error.userMessage = 'Produkten saknar referensbilder för visualiseringen.';
          throw error;
        }

        const results = await Promise.allSettled(
          referenceImageUrls.slice(0, 4).map((url, index) => this.fetchReferenceImage(url, index, signal))
        );
        if (signal.aborted) throw new DOMException('The operation was aborted', 'AbortError');

        const successfulImages = results
          .filter((result) => result.status === 'fulfilled')
          .map((result) => result.value);

        if (!successfulImages.length) {
          const error = new Error('All product reference image downloads failed');
          error.userMessage = 'Produktens referensbilder kunde inte hämtas. Försök igen.';
          throw error;
        }

        return successfulImages;
      }

      async fetchReferenceImage(url, index, signal) {
        return this.runWithTimeout(
          signal,
          15000,
          'En referensbild tog för lång tid att hämta.',
          async (requestSignal) => {
            const response = await fetch(url, { credentials: 'omit', signal: requestSignal });
            if (!response.ok) throw new Error(`Reference image request failed with status ${response.status}`);

            const blob = await response.blob();
            if (!blob.size || !blob.type.startsWith('image/')) {
              throw new Error('Reference image response was not a supported image');
            }

            return {
              blob,
              filename: this.getReferenceImageFilename(url, index, blob.type),
            };
          }
        );
      }

      getReferenceImageFilename(url, index, mimeType) {
        const extensionByType = {
          'image/avif': 'avif',
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/webp': 'webp',
        };
        const extension = extensionByType[mimeType] || 'jpg';
        let basename = `reference-${index + 1}`;

        try {
          const pathname = new URL(url, window.location.href).pathname;
          const filename = decodeURIComponent(pathname.split('/').pop() || '');
          basename = filename.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '-') || basename;
        } catch (error) {
          // Keep the deterministic fallback filename.
        }

        return `${basename}.${extension}`;
      }

      async runWithTimeout(signal, duration, userMessage, task) {
        const requestController = new AbortController();
        let timedOut = false;
        const abortRequest = () => requestController.abort();

        if (signal.aborted) {
          requestController.abort();
        } else {
          signal.addEventListener('abort', abortRequest, { once: true });
        }

        const timeout = window.setTimeout(() => {
          timedOut = true;
          requestController.abort();
        }, duration);

        try {
          return await task(requestController.signal);
        } catch (error) {
          if (timedOut) {
            const timeoutError = new Error('The request timed out');
            timeoutError.name = 'TimeoutError';
            timeoutError.userMessage = userMessage;
            throw timeoutError;
          }
          throw error;
        } finally {
          window.clearTimeout(timeout);
          signal.removeEventListener('abort', abortRequest);
        }
      }

      createVisualizationApiError(response, responseBody) {
        const error = new Error(`Visualization failed with status ${response.status}`);
        const errorDetails = JSON.stringify(responseBody || '').toLowerCase();

        if (response.status === 429) {
          error.userMessage = 'Du har nått gränsen för nya visualiseringar just nu. Försök igen om en stund.';
        } else if (
          errorDetails.includes('moderation') ||
          errorDetails.includes('content_policy') ||
          errorDetails.includes('safety')
        ) {
          error.userMessage = 'Bilden eller instruktionen kunde inte godkännas. Prova ett annat foto eller en annan beskrivning.';
        } else if (response.status === 400) {
          error.userMessage = 'Visualiseringen kunde inte skapas. Kontrollera dina val och försök igen.';
        } else if (response.status >= 500) {
          error.userMessage = 'Visualiseringen kunde inte skapas. Försök igen om en stund.';
        } else {
          error.userMessage = 'Visualiseringen kunde inte skapas. Försök igen.';
        }

        return error;
      }

      parseVisualizationResponse(responseBody) {
        if (
          !responseBody ||
          typeof responseBody.imageBase64 !== 'string' ||
          !responseBody.imageBase64.trim() ||
          typeof responseBody.mimeType !== 'string' ||
          !responseBody.mimeType.startsWith('image/')
        ) {
          const error = new Error('Visualization returned an invalid response');
          error.userMessage = 'Bildtjänsten gav ett ogiltigt svar. Försök igen.';
          throw error;
        }

        try {
          return {
            imageBlob: this.base64ToBlob(responseBody.imageBase64, responseBody.mimeType),
            mimeType: responseBody.mimeType,
          };
        } catch (error) {
          const malformedResponseError = new Error('Visualization returned invalid base64 image data');
          malformedResponseError.userMessage = 'Bildtjänsten gav ett ogiltigt bildsvar. Försök igen.';
          throw malformedResponseError;
        }
      }

      base64ToBlob(imageBase64, mimeType) {
        const encodedImage = imageBase64.includes(',') ? imageBase64.split(',').pop() : imageBase64;
        const binaryImage = window.atob(encodedImage.replace(/\s/g, ''));
        const chunks = [];

        for (let offset = 0; offset < binaryImage.length; offset += 1024) {
          const slice = binaryImage.slice(offset, offset + 1024);
          const bytes = new Uint8Array(slice.length);
          for (let index = 0; index < slice.length; index += 1) {
            bytes[index] = slice.charCodeAt(index);
          }
          chunks.push(bytes);
        }

        return new Blob(chunks, { type: mimeType });
      }

      cancelPipeline() {
        if (this.pipelineController) this.pipelineController.abort();
        this.pipelineController = null;
        this.clearGenerationLoadingTimers();
        if (this.state) {
          this.state.analysisPending = false;
          this.state.generationPending = false;
        }
      }

      reset(shouldFocusPicker = false) {
        const wasExpanded = this.state.expanded;
        this.cancelPipeline();
        this.preparationId += 1;
        this.revokeUploadedImage();
        this.revokeResultImage();
        this.state = this.createInitialState();
        this.state.expanded = wasExpanded;
        this.fileInput.value = '';
        this.renderSurfaceOptions([]);
        this.customPromptInput.value = '';
        this.updateCustomPromptCounter();
        this.setComparisonPosition(50);
        this.hideError();
        this.render();

        if (shouldFocusPicker) {
          const chooseButton = this.root.querySelector('[data-choose-photo]');
          if (chooseButton) chooseButton.focus();
        }
      }

      revokeUploadedImage() {
        if (this.state && this.state.uploadedImage && this.state.uploadedImage.previewUrl) {
          URL.revokeObjectURL(this.state.uploadedImage.previewUrl);
        }
      }

      revokeResultImage() {
        if (this.state && this.state.resultImage) {
          URL.revokeObjectURL(this.state.resultImage);
        }
      }

      setComparisonPosition(value) {
        if (this.comparison) {
          this.comparison.style.setProperty('--comparison-position', `${value}%`);
        }
      }

      downloadResult() {
        if (!this.state || !this.state.resultImage) return;

        const productHandle = this.productConfig.handle || 'produkt';
        const extensionByType = {
          'image/avif': 'avif',
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/webp': 'webp',
        };
        const extension = extensionByType[this.state.resultImageMimeType] || 'png';
        const link = document.createElement('a');

        link.href = this.state.resultImage;
        link.download = `kakelexpress-${productHandle}-visualisering.${extension}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      showError(message) {
        this.error.textContent = message;
        this.error.hidden = false;
      }

      hideError() {
        this.error.textContent = '';
        this.error.hidden = true;
      }

      updateCustomPromptCounter() {
        if (!this.customPromptInput || !this.customPromptCounter) return;
        const maximum = this.customPromptInput.maxLength > 0 ? this.customPromptInput.maxLength : 150;
        this.customPromptCounter.textContent = `${this.customPromptInput.value.length}/${maximum}`;
      }

      render() {
        const hasImage = Boolean(this.state.uploadedImage);
        const analysisPending = this.state.analysisPending;
        const generationPending = this.state.generationPending;
        const pipelinePending = analysisPending || generationPending;
        const loadingMode = generationPending ? 'generation' : analysisPending ? 'analysis' : null;
        const showEditor = this.state.pane === 'configure' || (this.state.pane === 'loading' && analysisPending);
        const showGeneration = this.state.pane === 'loading' && generationPending && hasImage;
        const showResult = this.state.pane === 'result' && Boolean(this.state.resultImage);

        this.dataset.expanded = String(this.state.expanded);
        this.dataset.pane = this.state.pane;
        if (loadingMode) {
          this.dataset.loadingMode = loadingMode;
        } else {
          delete this.dataset.loadingMode;
        }
        if (this.launchView) this.launchView.hidden = this.state.expanded;
        if (this.flow) this.flow.hidden = !this.state.expanded;
        if (this.openButton) this.openButton.setAttribute('aria-expanded', String(this.state.expanded));
        this.uploadView.hidden = this.state.pane !== 'upload';
        this.editorView.hidden = !showEditor;
        this.generationView.hidden = !showGeneration;
        this.resultView.hidden = !showResult;

        if (hasImage) {
          const { previewUrl, width, height } = this.state.uploadedImage;
          this.previewImage.src = previewUrl;
          this.previewImage.width = width || 1;
          this.previewImage.height = height || 1;
          this.generationImage.src = previewUrl;
          this.generationImage.width = width || 1;
          this.generationImage.height = height || 1;
          this.generationCanvas.style.setProperty('--generation-aspect-ratio', `${width || 1} / ${height || 1}`);
        } else {
          this.previewImage.removeAttribute('src');
          this.generationImage.removeAttribute('src');
          this.generationCanvas.style.removeProperty('--generation-aspect-ratio');
        }

        this.editorView.setAttribute(
          'aria-busy',
          analysisPending || this.state.imagePreparing ? 'true' : 'false'
        );
        this.imagePreparingStatus.hidden = !this.state.imagePreparing;
        this.loadingStatus.hidden = !analysisPending;
        this.loadingMessage.textContent = this.state.loadingMessage || 'Vi skapar din visualisering…';
        this.generationMessage.textContent = this.state.loadingMessage || 'Förbereder material…';
        if (this.generationTip && this.generationTipText) {
          const generationTips = this.getGenerationTips();
          const generationTipIndex = this.state.generationTipIndex % generationTips.length;
          this.generationTipText.textContent = generationTips[generationTipIndex];
          this.generationTip.setAttribute('data-tip-variant', String(generationTipIndex % 2));
        }
        this.surfaceFieldset.hidden = this.state.surfaces.length === 0;
        const analysisReady = this.state.analysisStatus === 'ready';
        const canAnalyze =
          hasImage &&
          !this.state.imagePreparing &&
          !pipelinePending &&
          (this.state.analysisStatus === 'idle' || this.state.analysisStatus === 'error');
        const canGenerate =
          hasImage &&
          analysisReady &&
          Boolean(this.state.selectedSurface) &&
          !this.state.imagePreparing &&
          !pipelinePending;
        this.generateButton.disabled = !(canAnalyze || canGenerate);
        this.generateButton.classList.toggle('is-loading', generationPending);
        const generateButtonText = this.generateButton.querySelector('[data-generate-button-text]');
        if (generateButtonText) {
          generateButtonText.textContent =
            this.state.analysisStatus === 'error'
              ? 'Analysera igen'
              : this.productConfig.ui.generateButtonText;
        }

        this.surfaceInputs.forEach((input) => {
          input.disabled = pipelinePending;
        });
        this.customPromptInput.disabled = pipelinePending;
        this.updateCustomPromptCounter();
        this.replaceButton.disabled = pipelinePending;
        this.removeButton.disabled = pipelinePending;
        if (this.disclaimer) this.disclaimer.hidden = !hasImage;

        if (showResult) {
          const imageWidth = this.state.uploadedImage.width || 1;
          const imageHeight = this.state.uploadedImage.height || 1;
          this.beforeImage.src = this.state.uploadedImage.previewUrl;
          this.beforeImage.width = imageWidth;
          this.beforeImage.height = imageHeight;
          this.resultImage.src = this.state.resultImage;
          this.resultImage.width = imageWidth;
          this.resultImage.height = imageHeight;
          const selectedSurface = this.state.surfaces.find((surface) => surface.value === this.state.selectedSurface);
          const surfaceLabel = selectedSurface ? selectedSurface.label : '';
          this.resultSurface.textContent = surfaceLabel
            ? `Visualiserat resultat · ${surfaceLabel}`
            : 'Visualiserat resultat';
        } else {
          this.beforeImage.removeAttribute('src');
          this.resultImage.removeAttribute('src');
        }
      }
    }
  );
}
